/**
 * Any Tickets API calls with React Query.
 */
import { ListTicketDropdownCancelToken, GetlistTicketForDashboard, GetTicketSummaryCancelToken } from "../../services/DashBoardServices";
import { TABLE_DATE_TIME_FORMAT } from "../../utils/Constants";
import { convertToLocalTime } from "../../utils/commonFunction";
import { QUERY_CACHE_PROFILES, QUERY_KEYS } from "../queryConsts";
import { useBaseReactQuery } from "./useBaseReactQuery";

const EMPTY_TICKETS_DROPDOWN = Object.freeze({
    studentStatus: [],
    level: [],
    department: [],
    category: [],
    user: [],
    showing: []
});

const EMPTY_TICKETS_LIST = Object.freeze({
    ticketsList: [],
    totalRows: 0
});

const EMPTY_TICKETS_SUMMARY = Object.freeze({});

/**
 * Tickets Dropdown - PERSISTED
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useTicketsDropdown(filters) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.tickets.root, QUERY_KEYS.tickets.ticketsDropdown, filters],
        async () => {
            const { data } = await ListTicketDropdownCancelToken(filters);

            return {
                level: data.level ?? [],
                studentStatus: data.studentStatus ?? [],
                department: data.departments ?? [],
                category: data.category ?? [],
                user: data.assignUser ?? [],
                showing: []
            };
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.LOOKUP
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_TICKETS_DROPDOWN
    };
}

/**
 * React Query hook for Tickets lists.
 * The requestData should already be in the format expected by the API.
 *
 * @param {object} requestData Request body passed directly to the API.
 * @returns {DefinedUseQueryResult}
 */
export function useTicketsList(requestData) {
    const query = useBaseReactQuery(
        [
            QUERY_KEYS.tickets.root,
            QUERY_KEYS.tickets.ticketsList,
            requestData
        ],
        async () => {
            const { data } = await GetlistTicketForDashboard(requestData);

            return {
                ticketsList: (data?.data ?? []).map((ticketRow) => ({
                    ...ticketRow,
                    date_add: ticketRow.date_add
                        ? convertToLocalTime(ticketRow.date_add, TABLE_DATE_TIME_FORMAT)
                        : "",
                    last_update: ticketRow.last_update
                        ? convertToLocalTime(ticketRow.last_update, TABLE_DATE_TIME_FORMAT)
                        : "",
                })),
                totalRows: data?.total ?? 0
            };
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.TABLE
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_TICKETS_LIST
    };
}

/**
 * React Query hook for Tickets Summary.
 * The requestData should already be in the format expected by the API.
 *
 * @param {object} requestData Request body passed directly to the API.
 * @returns {DefinedUseQueryResult}
 */
export function useTicketsSummary(summaryData) {

    const query = useBaseReactQuery(
        [
            QUERY_KEYS.tickets.root,
            QUERY_KEYS.tickets.ticketsSummary,
            summaryData
        ],
        async () => {
            const { data } = await GetTicketSummaryCancelToken(summaryData);
    
            return data;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_TICKETS_SUMMARY
    };
}
